import { useEffect, useState } from 'react';
import api from '../../services/api';

/**
 * Je Beitragsidee das Medium, das in Listen fuer sie steht (Backend:
 * GET /api/media-assets/vorschau-je-idee?postIdeaIds=a,b,c, hoechstens 200
 * Ids je Abfrage). Hier in Paketen zu 100 Ids. Ergebnisse bleiben eine Minute
 * modulweit gemerkt, damit Wochenplan, Terminieren und Beitragsideen beim
 * Wechseln nicht jedes Mal neu fragen.
 */

export interface IdeenVorschau {
  assetId: string;
  kind: string;
  /** true = es gibt ein Vorschaubild, sonst nur das Symbol. */
  vorschau: boolean;
}

export type VorschauJeIdee = Record<string, IdeenVorschau | null>;

const PAKET = 100;
const GUELTIG_MS = 60_000;
const gemerkt = new Map<string, { wert: IdeenVorschau | null; zeit: number }>();

const gueltig = (w: unknown): w is IdeenVorschau =>
  !!w && typeof w === 'object' && typeof (w as IdeenVorschau).assetId === 'string';

function ausGemerkt(ids: string[]): VorschauJeIdee {
  const karte: VorschauJeIdee = {};
  for (const id of ids) {
    const e = gemerkt.get(id);
    if (e) karte[id] = e.wert;
  }
  return karte;
}

/** Nur fuer Tests und nach Uploads: gemerkte Zuordnungen verwerfen. */
export function vorschauJeIdeeVergessen(): void {
  gemerkt.clear();
}

export function useVorschauJeIdee(ideaIds: Array<string | null | undefined>): VorschauJeIdee {
  const schluessel = Array.from(new Set(ideaIds.filter((i): i is string => Boolean(i)))).sort().join(',');
  const [karte, setKarte] = useState<VorschauJeIdee>(() => ausGemerkt(schluessel ? schluessel.split(',') : []));

  useEffect(() => {
    const ids = schluessel ? schluessel.split(',') : [];
    const jetzt = Date.now();
    const offen = ids.filter((id) => {
      const e = gemerkt.get(id);
      return !e || jetzt - e.zeit > GUELTIG_MS;
    });
    setKarte(ausGemerkt(ids));
    if (offen.length === 0) return undefined;

    let aktiv = true;
    (async () => {
      for (let i = 0; i < offen.length; i += PAKET) {
        const paket = offen.slice(i, i + PAKET);
        try {
          const antwort = await api.get('/media-assets/vorschau-je-idee', { params: { postIdeaIds: paket.join(',') } });
          const daten = antwort?.data;
          if (!daten || typeof daten !== 'object' || Array.isArray(daten)) continue;
          const zeit = Date.now();
          for (const id of paket) {
            const w = Object.prototype.hasOwnProperty.call(daten, id) ? (daten as Record<string, unknown>)[id] : null;
            gemerkt.set(id, { wert: gueltig(w) ? w : null, zeit });
          }
        } catch {
          // Ohne Vorschaubilder weiter; die Liste selbst bleibt benutzbar.
        }
      }
      if (aktiv) setKarte(ausGemerkt(ids));
    })();
    return () => { aktiv = false; };
  }, [schluessel]);

  return karte;
}

export default useVorschauJeIdee;
